import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { deleteUserAccountMutation } from "~/modules/lookup/mutations";
import { lookupUserByIdQuery } from "~/modules/lookup/queries";

export const Route = createFileRoute("/_dash/users/delete-account/$userId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.userId) {
      throw new Error("User ID is required");
    }

    queryClient.prefetchQuery(
      lookupUserByIdQuery.queryOptions({ pathParams: { userId: params.userId } })
    );
  },
});

function RouteComponent() {
  return (
    <PermissionGuard permission="users:edit">
      <DeleteAccountPage />
    </PermissionGuard>
  );
}

function DeleteAccountPage() {
  const navigate = Route.useNavigate();
  const { userId } = Route.useParams();
  const [reason, setReason] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const { data } = lookupUserByIdQuery.useSuspenseQuery({
    pathParams: { userId },
  });

  const { mutate, isPending } = deleteUserAccountMutation.useMutation({
    onSuccess() {
      toast.success("Account deleted");
      navigate({ to: "/users/view/$userId", params: { userId } });
    },
    onError() {
      setShowConfirm(false);
      toast.error("Failed to delete the account. Please try again.");
    },
  });

  const userLabel = data?.user?.name || data?.user?.email || userId;
  const alreadyDeleted = Boolean(data?.user?.deletedAt);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/users/view/$userId"
          params={{ userId }}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to User
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-bold">Delete Account</h1>
      </div>

      <div className="max-w-lg space-y-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
          <p className="font-medium">Warning</p>
          <p className="mt-1">
            This will mark the account of <span className="font-semibold">{userLabel}</span> as
            deleted. The user will lose access to their account.
          </p>
        </div>

        {alreadyDeleted ? (
          <p className="text-sm text-muted-foreground">This account is already deleted.</p>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                placeholder="Why is this account being deleted?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <Button
              variant="destructive"
              disabled={!reason.trim()}
              onClick={() => setShowConfirm(true)}
            >
              Delete Account
            </Button>
          </>
        )}
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Account Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the account of{" "}
              <span className="font-semibold">{userLabel}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => mutate({ pathParams: { userId }, params: { reason: reason.trim() } })}
              disabled={isPending}
            >
              {isPending ? "Deleting..." : "Delete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
